import React from 'react';
import { fmtBudgetRange } from '../lib/budget';
import { htmlToPlainText } from '../lib/htmlText';
import InfoIcon from './InfoIcon';

// Install / De-rig detail notes are now rich text (stored as HTML). A note may
// therefore be HTML or legacy plain text; render accordingly so suppliers
// never see raw tags. Same detector used in BriefDetail / SupplierResponse.
const looksLikeHtml = (s) => typeof s === 'string' && /<\/?[a-z][\s\S]*>/i.test(s);

// Plain-English explanations for the supplier brief-detail page, keyed by
// fact. Rendered as a ⓘ beside the label ONLY when the panel is asked to
// (showInfo) – the AddBrief preview deliberately stays clean.
//
// Brief-detail §6: reserve tips for genuinely non-obvious things. Every other
// fact label (Event date, Access, Location, Client, Rig/De-rig, …) is
// self-explanatory or explained by its own value, so tipping it is noise. Only
// the Response deadline keeps a tip here; combined with the Decline and
// extension tips elsewhere on the page that holds the total at three.
const FACT_INFO = {
  deadline: { heading: 'Response deadline', body: 'The date and time Fusion 2K needs your response by. The sooner you reply, the more time the team has to consider you.' },
};

// Supplier-facing "facts panel" – the bordered card that surfaces the
// key facts about a brief (deadline, client, event date, etc.).
//
// Used in two places:
//   1. SupplierResponse – the supplier's actual brief detail page
//   2. AddBrief – as the live preview in the "What the supplier sees"
//      panel, so staff can see exactly what each visibility toggle
//      changes
//
// Both surfaces render this same component so they stay in lockstep –
// any tweak to the panel automatically shows up in the preview too.
//
// The brief argument is shaped like a row from the briefs table –
// AddBrief passes its form state as-is because the form fields share
// names with the columns.
//
// Visibility rules (also documented on the AddBrief panel + the
// supplier-visibility migrations):
//   - Response Deadline always renders if set – no flag, suppliers
//     must see when they need to respond. (The brief body + attachments
//     are the other always-on essentials, rendered by SupplierResponse
//     itself, not here.)
//   - Every other fact – Project Number / Project Name / Client /
//     Category / Event Date / Delivery Date / Delivery Address /
//     Location / Budget / Rig / De-rig – renders only when (value
//     present AND non-empty) AND (its show_* flag is true)
//   - Defaults preserve prior behaviour: show_project_name + the
//     original four (client/event/delivery date/location) default TRUE;
//     show_budget + the newly-gated fields (project number, category,
//     delivery address, rig, de-rig) default FALSE
//   - delivery_date_na overrides Delivery Date – if set, the date
//     doesn't render even when the value column is populated
//   - Rig / De-rig show their dates as the value and the details text
//     as a note line beneath

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// event_live_date and expected_required_by_date are DATE columns
// (YYYY-MM-DD). Slice rather than new Date() to avoid timezone shifts
// that can push the rendered day off by one.
//
// Day is zero-padded ("04 Sep 2026" not "4 Sep 2026") to match the
// shared lib/dates fmtDate output – otherwise this panel renders a
// different day style to every other date on the brief detail page.
// MONTHS uses "Sep" (3 letters) to match the lib/dates normaliseShortMonth
// fix; en-GB's locale would render "Sept" for September otherwise.
const pad2 = (n) => String(n).padStart(2, '0');
const fmtDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return `${pad2(d)} ${MONTHS[m - 1]} ${y}`;
};

const fmtDateTime = (iso) => {
  if (!iso) return null;
  const dt = new Date(iso);
  const day = pad2(dt.getDate());
  const mon = MONTHS[dt.getMonth()];
  const yr  = dt.getFullYear();
  const hh = pad2(dt.getHours());
  const mm = pad2(dt.getMinutes());
  return `${day} ${mon} ${yr}, ${hh}:${mm}`;
};

// Rig / de-rig dates carry a time (the form captures HH:mm). Render via
// `new Date` so a UTC-stored timestamp converts back to the viewer's
// local time – matches BriefDetail's admin-side rendering and stops the
// supplier seeing a different hour to the staffer who wrote the brief.
//
// (Earlier this used a literal pre-T-split parse on the assumption that
// the stored value was naive-local. Rig fields are timestamptz, so a
// 10:00 BST entry becomes "09:00:00+00:00" on read – the literal parse
// surfaced the UTC hour, not the original local. Hence the previous
// admin/supplier mismatch.)
const fmtDateMaybeTime = (iso) => {
  if (!iso) return null;
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return null;
  const day = pad2(dt.getDate());
  const mon = MONTHS[dt.getMonth()];
  const yr = dt.getFullYear();
  const hh = pad2(dt.getHours());
  const mm = pad2(dt.getMinutes());
  // A 00:00 reading means the source carried date-only (no time) – fall
  // back to the date string so we don't surface a meaningless midnight.
  if (hh === '00' && mm === '00' && !/T\d{2}:\d{2}/.test(String(iso))) {
    return `${day} ${mon} ${yr}`;
  }
  return `${day} ${mon} ${yr}, ${hh}:${mm}`;
};

// Local timezone abbreviation (e.g. "BST" / "GMT") – Create Brief captures the
// deadline in local time, and a bare "17:00" is ambiguous without it (§7).
const tzAbbr = () => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || '';
  } catch { return ''; }
};

// Relative deadline for the countdown line. §7: matches the dashboard's
// phrasing ("Due in N days") and its escalation bands – neutral while
// comfortable (>7d), amber approaching (≤7d), red imminent (≤2d) or overdue –
// so urgency reads the same on both screens. Calendar-day diff so the count
// matches what a human would say (a partial day isn't rounded up to a whole).
const computeRemaining = (iso) => {
  if (!iso) return null;
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return null;
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(dt) - startOfDay(new Date())) / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`, tone: 'red' };
  if (days === 0) return { label: 'Due today', tone: 'red' };
  if (days === 1) return { label: 'Due tomorrow', tone: 'red' };
  if (days <= 2) return { label: `Due in ${days} days`, tone: 'red' };
  if (days <= 7) return { label: `Due in ${days} days`, tone: 'amber' };
  return { label: `Due in ${days} days`, tone: 'neutral' };
};

// Build the facts list. Pure – exported separately so the visibility
// panel on AddBrief can introspect which facts are about to render.
// deadlineOverride: when the supplier has a per-recipient response_deadline
// (e.g. Fusion granted them an extension), pass it so the Response Deadline
// fact + countdown reflect THEIR deadline rather than the brief's base one.
// awarded: the awarded-brief state (fix-list §2). The supplier is now
// delivering the work, so the response deadline (long passed, irrelevant) is
// dropped entirely and the card leads with the dates that now matter – event,
// then delivery/access, then location and rig.
export function buildSupplierFacts(brief, deadlineOverride = null, awarded = false) {
  if (!brief) return [];
  const facts = [];
  if (brief.required_response_time && !awarded) {
    const effectiveDeadline = deadlineOverride || brief.required_response_time;
    // Terminal-state suppression (2026-06 deadline-suppression rule B2):
    // once the outcome is settled (awarded / completed / closed /
    // cancelled), the response-deadline pill stops surfacing the red
    // "DEADLINE PASSED" tone. The deadline itself still renders so the
    // supplier sees what it WAS, but the urgency annotation is noise
    // once a winner has been picked.
    const terminal = brief.status === 'closed' || brief.status === 'cancelled' || !!brief.awarded_supplier_id;
    facts.push({
      key: 'deadline',
      label: 'Response Deadline',
      labelEmphasis: true,
      // §7: show the timezone so a bare time isn't ambiguous.
      value: `${fmtDateTime(effectiveDeadline)}${tzAbbr() ? ` ${tzAbbr()}` : ''}`,
      remaining: terminal ? null : computeRemaining(effectiveDeadline),
    });
  }
  // Questions close sits beside the response deadline – it's a deadline too, and
  // belongs with the other one (supplier-questions brief §4). Suppressed once
  // the outcome is settled (same as the response deadline).
  if (brief.questions_close && !awarded && brief.status !== 'closed' && brief.status !== 'cancelled' && !brief.awarded_supplier_id) {
    facts.push({
      key: 'questions_close',
      label: 'Questions Close',
      value: `${fmtDateTime(brief.questions_close)}${tzAbbr() ? ` ${tzAbbr()}` : ''}`,
    });
  }
  // FS/project number is INTERNAL to Fusion 2K – never shown to suppliers
  // (submission-confirmation §2). The fact is intentionally not built here even
  // if show_project_number is on; suppliers identify a brief by its name.
  if ((brief.show_project_name ?? true) && brief.fusion_project_name && String(brief.fusion_project_name).trim()) {
    facts.push({ key: 'project_name', label: 'Project Name', value: String(brief.fusion_project_name).trim() });
  }
  if ((brief.show_client ?? true) && brief.client && String(brief.client).trim()) {
    facts.push({ key: 'client', label: 'Client', value: String(brief.client).trim() });
  }
  if ((brief.show_category ?? false) && brief.brief_category && String(brief.brief_category).trim()) {
    facts.push({ key: 'category', label: 'Category', value: String(brief.brief_category).trim() });
  }
  if ((brief.show_event_date ?? true) && brief.event_live_date) {
    const start = fmtDate(brief.event_live_date);
    const end   = fmtDate(brief.event_live_end_date);
    facts.push({
      key: 'event_date',
      label: 'Event Date',
      value: end && end !== start ? `${start} – ${end}` : start,
    });
  }
  if ((brief.show_delivery_date ?? true) && brief.expected_required_by_date && !brief.delivery_date_na) {
    facts.push({
      key: 'delivery_date',
      label: 'Access',
      // "from" (expected_required_by_date) with an optional "to" range.
      value: brief.delivery_access_to_date
        ? `${fmtDate(brief.expected_required_by_date)} – ${fmtDate(brief.delivery_access_to_date)}`
        : fmtDate(brief.expected_required_by_date),
    });
  }
  if ((brief.show_delivery_address ?? false) && brief.delivery_address && String(brief.delivery_address).trim()) {
    facts.push({ key: 'delivery_address', label: 'Access Address', value: String(brief.delivery_address).trim() });
  }
  if ((brief.show_location ?? true) && brief.location && String(brief.location).trim()) {
    facts.push({
      key: 'location',
      label: 'Location',
      value: String(brief.location).trim(),
    });
  }
  if (brief.show_budget ?? false) {
    // 2026-06: budget can now be a range – use the shared formatter
    // so the supplier-facing Brief Facts panel reads consistently
    // with admin BriefDetail. "+ VAT" appended via withVat option.
    const value = fmtBudgetRange(brief, { withVat: true });
    if (value) {
      facts.push({ key: 'budget', label: 'Budget', value });
    }
  }
  // End-before-start sanity check. Returns a warning string when the pair
  // contradicts itself so the renderer can flag it; falsy when fine or
  // when one half is missing (no implicit pair, no warning to raise).
  const dateOrderWarning = (startIso, endIso, label) => {
    if (!startIso || !endIso) return null;
    const s = new Date(startIso);
    const e = new Date(endIso);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
    if (e.getTime() < s.getTime()) return `${label} end is before start – schedule needs fixing.`;
    return null;
  };

  // Rig / De-rig – dates and details shown together as one fact. Gated on
  // there being something to show (a date or details); the date columns are
  // only populated when the rig/de-rig toggle was on at save time.
  const rigDates = (() => {
    const start = fmtDateMaybeTime(brief.rig_start_date);
    const end = fmtDateMaybeTime(brief.rig_end_date);
    return start ? (end && end !== start ? `${start} – ${end}` : start) : null;
  })();
  if ((brief.show_rig ?? false) && (rigDates || (brief.rig_details && String(brief.rig_details).trim()))) {
    facts.push({
      key: 'rig',
      label: 'Rig',
      value: rigDates || 'Required',
      note: brief.rig_details ? String(brief.rig_details).trim() : null,
      warning: dateOrderWarning(brief.rig_start_date, brief.rig_end_date, 'Rig'),
    });
  }
  const derigDates = (() => {
    const start = fmtDateMaybeTime(brief.derig_start_date);
    const end = fmtDateMaybeTime(brief.derig_end_date);
    return start ? (end && end !== start ? `${start} – ${end}` : start) : null;
  })();
  if ((brief.show_derig ?? false) && (derigDates || (brief.derig_details && String(brief.derig_details).trim()))) {
    facts.push({
      key: 'derig',
      label: 'De-rig',
      value: derigDates || 'Required',
      note: brief.derig_details ? String(brief.derig_details).trim() : null,
      warning: dateOrderWarning(brief.derig_start_date, brief.derig_end_date, 'De-rig'),
    });
  }
  // §2 – in the awarded state, lead with the dates a delivering supplier needs.
  // Facts not in the priority list keep their build order, after the leaders.
  if (awarded) {
    const order = ['event_date', 'delivery_date', 'location', 'rig', 'derig', 'client', 'project_name', 'budget', 'delivery_address', 'category'];
    const rank = (k) => { const i = order.indexOf(k); return i === -1 ? order.length : i; };
    facts.sort((a, b) => rank(a.key) - rank(b.key));
  }
  return facts;
}

// Amendment treatment – when `previousBrief` is supplied (the brief as the
// supplier last saw it, with amendable fields rewound to the last-seen
// snapshot) each fact whose displayed value or note has changed is rendered
// with an amber border + "Changed" chip + the previous value struck-through
// below the current one. Notes (rig / de-rig details) participate too – a
// detail rewrite without a date change still flags the fact as changed.
// `narrow` – the panel is rendering in a column rather than across the page.
// Three metadata columns inside the Create-Brief live preview left almost
// every value wrapping onto two or three lines (tidy-up §11); at two columns
// most land on one. The supplier's own view is full width and keeps three, so
// this is a prop rather than a change to the shared layout.
export default function SupplierBriefFactsPanel({ brief, previousBrief = null, showInfo = false, raised = false, deadlineOverride = null, showWarnings = false, awarded = false, narrow = false }) {
  const facts = buildSupplierFacts(brief, deadlineOverride, awarded);
  if (facts.length === 0) return null;
  // Pass the override to the previous snapshot too, so a granted extension
  // doesn't get mis-flagged as an amendment "Changed" on the deadline fact.
  const prevByKey = previousBrief
    ? new Map(buildSupplierFacts(previousBrief, deadlineOverride, awarded).map(f => [f.key, f]))
    : new Map();
  // Split rig + de-rig out of the regular flex-wrap row. They carry
  // long-form notes (rig_details / derig_details) that bloat the cell
  // to a width neighbours can't match, so the previous flex-wrap
  // pushed de-rig orphaned onto its own line while rig left a wide
  // dead zone next to it. Rendering them as a 2-column pair beneath
  // the compact facts keeps the two parallel concepts visually paired.
  const simpleFacts = facts.filter(f => f.key !== 'rig' && f.key !== 'derig');
  const rigFact = facts.find(f => f.key === 'rig') || null;
  const derigFact = facts.find(f => f.key === 'derig') || null;
  const renderFact = (fact, opts = {}) => {
    const { compact = false } = opts;
    const prev = prevByKey.get(fact.key);
    const changed = !!previousBrief && prev && (prev.value !== fact.value || (prev.note || null) !== (fact.note || null));
    return (
      <div
        key={fact.key}
        className={`min-w-[130px] relative ${compact && opts.separator ? 'pr-6 border-r border-line' : ''} ${changed ? 'rounded-lg border border-[#E89A2E] bg-[#E89A2E]/[0.06] px-3 py-2' : ''}`}
      >
            <div className="flex items-start justify-between gap-2">
              <p className={`font-['Metropolis'] text-[9px] font-bold tracking-[0.18em] uppercase mb-1 ${fact.labelEmphasis ? 'text-[#7A4E10]' : 'text-[#51534A]'}`}>
                {fact.label}
                {showInfo && FACT_INFO[fact.key] && (
                  <InfoIcon heading={FACT_INFO[fact.key].heading} ariaLabel={`What does ${FACT_INFO[fact.key].heading} mean?`}>
                    {FACT_INFO[fact.key].body}
                  </InfoIcon>
                )}
              </p>
              {changed && (
                <span className="font-['Metropolis'] text-[8px] font-bold tracking-[0.18em] uppercase text-[#131313] bg-[#E89A2E] rounded-md px-1.5 py-0.5">Changed</span>
              )}
            </div>
            <p className="font-['Metropolis'] text-[15px] font-bold text-ink">{fact.value}</p>
            {changed && prev.value !== fact.value && prev.value && (
              <p className="font-['Metropolis'] text-[12px] text-[#51534A] line-through mt-0.5">{prev.value}</p>
            )}
            {fact.note && (
              looksLikeHtml(fact.note)
                // Normalise non-breaking spaces to regular spaces before render:
                // some stored rig/de-rig HTML has EVERY space as &nbsp;, which is
                // unwrappable and overflowed the column (overlapping the neighbour).
                // break-words is belt-and-braces for any other long token.
                ? <div className="sp-rich-note font-['Metropolis'] text-[12px] text-fusion-mid-grey mt-1 max-w-[260px] leading-snug break-words" dangerouslySetInnerHTML={{ __html: fact.note.replace(/(&nbsp;| )/g, ' ') }} />
                : <p className="font-['Metropolis'] text-[12px] text-fusion-mid-grey mt-1 max-w-[260px] whitespace-pre-wrap leading-snug break-words">{fact.note}</p>
            )}
            {changed && (prev.note || null) !== (fact.note || null) && prev.note && (
              /* "Removed" indicator – flatten any HTML to plain text so the
                 strikethrough reads cleanly rather than showing tags. */
              <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey line-through mt-0.5 max-w-[260px] whitespace-pre-wrap leading-snug">{looksLikeHtml(prev.note) ? htmlToPlainText(prev.note) : prev.note}</p>
            )}
            {fact.remaining && (
              <p className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.12em] uppercase mt-1 ${fact.remaining.tone === 'red' ? 'text-[#E0290A]' : fact.remaining.tone === 'amber' ? 'text-[#7A4E10]' : 'text-[#51534A]'}`}>
                {fact.remaining.label}
              </p>
            )}
            {/* BRIEF-28 §6a – internal schedule-order validation ("Rig end is
                before start …") is admin-only; never shown to suppliers. Off by
                default; the admin AddBrief preview opts in via showWarnings. */}
            {showWarnings && fact.warning && (
              <p
                role="alert"
                className="font-['Metropolis'] text-[11px] font-bold text-[#E0290A] mt-1.5 leading-snug inline-flex items-start gap-1"
                style={{ maxWidth: 240 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }}>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                {fact.warning}
              </p>
            )}
          </div>
    );
  };
  return (
    <div className={`${raised ? 'bg-white' : 'bg-white'} border border-line rounded-xl px-6 py-5`}>
      {/* Compact facts – deadline / project / client / event / etc.
          §4: a fixed-column GRID (was flex-wrap, which let each row split at
          different x-positions and read as a broken grid). Every fact sits on
          the same column template so rows line up; the column gap gives clean
          separation without the mis-aligning per-cell dividers. Collapses to
          two columns on narrow widths. */}
      {simpleFacts.length > 0 && (
        <div className={`grid gap-x-6 gap-y-4 ${narrow ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
          {simpleFacts.map((fact) => renderFact(fact, { compact: true }))}
        </div>
      )}
      {/* Rig + De-rig render beneath as a 2-column pair so the parallel
          concepts visually pair up. Single-column fallback when only one
          is present. Top border separates from the compact row. */}
      {(rigFact || derigFact) && (
        <div
          className={`mt-4 pt-4 grid gap-x-6 gap-y-4 ${rigFact && derigFact ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'} ${simpleFacts.length > 0 ? 'border-t border-line' : ''}`}
        >
          {rigFact && renderFact(rigFact)}
          {derigFact && renderFact(derigFact)}
        </div>
      )}
    </div>
  );
}
